import React from "react";
import { useLanguage } from "../../context/LanguageContext";
import { translations } from "../../i18n/translations";
import { motion } from "framer-motion";
import { FormValidationSummary, useFormValidation } from "../common/FormValidationSummary";
import type { FormStepProps } from "../../types/application";
import { isMobile } from "react-device-detect";
import { useState } from "react";
import "../../assets/gcweb-theme.css";
import "../../assets/gcweb-font-fix.css";

const PrioritiesEntitlementsStep: React.FC<FormStepProps> = ({ onNext }) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const validation = useFormValidation({ t });

  const handleSubmit = (e: React.FormEvent) => {
    validation.handleSubmit(e, onNext);
  };
  const [hasConfirmedNotice, setHasConfirmedNotice] = useState(false);

  if (isMobile)
    return (
      <motion.form
        ref={validation.formRef}
        className="wb-frmvld space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        {/* Header */}
        <div className="text-left">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t.consentHeader}
          </h2>
        </div>

        {/* Section: Data collection Consent */}
        <section className="alert alert-info">
          <h3>
            <strong>{t.notice}</strong>
          </h3>
          <ol className="mrgn-lft-lg lst-spcd">
            <li>{t.consent1}</li>
            <li>
              {t.consent2}
              <ol className="mrgn-lft-lg lst-spcd" style={{ listStyleType: "lower-alpha" }}>
                <li>{t.consent2a}</li>
                <li>{t.consent2b}</li>
                <li>{t.consent2c}</li>
                <li>{t.consent2d}</li>
                <li>{t.consent2e}</li>
              </ol>
            </li>
          </ol>
          <p>{t.consentBabble}</p>
        </section>

        {/* Confirmation Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t.confirmation}
          </h2>
          <p className="text-lg text-gray-900">
            <strong>{t.pleaseIndicatePreference}</strong>
          </p>
        </div>

        <fieldset className="gc-chckbxrdio">
          <legend className="wb-inv">{t.confirmation}</legend>
          <ul className="list-unstyled lst-spcd-2">
            <li className="checkbox">
              <input
                type="checkbox"
                name="ConsentStatus"
                id="consent-confirm"
                checked={hasConfirmedNotice}
                onChange={(e) => setHasConfirmedNotice(e.target.checked)}
                required
              />
              <label htmlFor="consent-confirm">{t.consentConfirmation}</label>
            </li>
          </ul>
        </fieldset>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
          <button
            type="submit"
            className="w-full sm:w-auto px-6 py-3 rounded-lg border-2 font-medium transition-colors primary-action-button"
          >
            {t.nextAiConsent}
          </button>
        </div>
      </motion.form>
    );
  else
    return (
      <motion.form
        ref={validation.formRef}
        className="wb-frmvld space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        {/* Section: Data collection Consent */}
        <section className="alert alert-info">
          <h3 className="h5">
            <strong>{t.notice}</strong>
          </h3>
          <ol className="mrgn-lft-lg lst-spcd">
            <li>{t.consent1}</li>
            <li>
              {t.consent2}
              <ol className="mrgn-lft-lg lst-spcd" style={{ listStyleType: "lower-alpha" }}>
                <li>{t.consent2a}</li>
                <li>{t.consent2b}</li>
                <li>{t.consent2c}</li>
                <li>{t.consent2d}</li>
                <li>{t.consent2e}</li>
              </ol>
            </li>
          </ol>
          <p>{t.consentBabble}</p>
        </section>

        {/* Confirmation Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t.confirmation}
          </h2>
          <p className="text-lg text-gray-900">
            <strong>{t.pleaseIndicatePreference}</strong>
          </p>
        </div>

        <fieldset className="gc-chckbxrdio">
          <legend className="wb-inv">{t.confirmation}</legend>
          <ul className="list-unstyled lst-spcd-2">
            <li className="checkbox">
              <input
                type="checkbox"
                name="ConsentStatus"
                id="consent-confirm-desktop"
                checked={hasConfirmedNotice}
                onChange={(e) => setHasConfirmedNotice(e.target.checked)}
                required
              />
              <label htmlFor="consent-confirm-desktop">
                {t.consentConfirmation}
              </label>
            </li>
          </ul>
        </fieldset>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
          <button
            type="submit"
            className="w-full sm:w-auto px-6 py-3 rounded-lg border-2 font-medium transition-colors primary-action-button"
          >
            {t.nextAiConsent}
          </button>
        </div>
      </motion.form>
    );
};

export default PrioritiesEntitlementsStep;
